import { useCallback, useEffect, useRef, useState } from 'react';
import BusinessIcon from '@mui/icons-material/Business';
import { Box, Button, Chip, Link, MenuItem, Popover, Stack, TextField, Typography } from '@mui/material';
import { DEFAULT_DROPBOX_ROOT, DROPBOX_FOLDER_STATUS } from 'dashboard409a/components/OperationsModal/dropbox.constants';
import { getDropboxFolderStatusConfig, stripDropboxRootPrefix } from 'dashboard409a/components/OperationsModal/dropbox.utils';
import {
  FOLDER_PICKER_PATH_SX,
  FOLDER_PICKER_POPOVER_PAPER_SX,
} from 'dashboard409a/components/OperationsModal/folderPickerPopover.styles';
import WorkboardFolderPicker from 'dashboard409a/components/OperationsModal/WorkboardFolderPicker';
import { toWorkboardRelativePrefixFromStorageReference } from 'dashboard409a/components/OperationsModal/workboardPath.utils';
import { ConditionalLoader, Section } from 'dashboard409a/pages/Operations/components';
import PersonnelRoleCard from 'dashboard409a/pages/Operations/components/ValuedInformation/PersonnelRoleCard';
import { AttioNotesService } from 'dashboard409a/services/attio/AttioNotesService';
import { Logger, splitJoinedValues } from 'utilities';
import AttioNotesButton from './AttioNotesButton';
import { CategoryBadge, ContactCardsRow, ContactSection, Container, HeaderRow, InfoRow, StyledLinkSpan } from './ValuedEntity.styles';
import { ValuedEntityProps } from './ValuedEntity.types';

const websiteHref = (domain: string) =>
  domain.startsWith('http://') || domain.startsWith('https://') ? domain : `https://${domain}`;

const ValuedEntity = ({
  valuedEntity,
  isLoading,
  companyFolder,
  dropboxRoot = DEFAULT_DROPBOX_ROOT,
  dropboxRootOptions = [DEFAULT_DROPBOX_ROOT],
  onDropboxRootChange,
  companyFolderStatus = 'idle',
  companyFolderMatchPaths = [],
  companyFolderSimilarMatchesCount = 0,
  isCompanyFolderSaveDisabled = false,
  onUpdateCompanyFolder,
}: ValuedEntityProps) => {
  const [companyFolderAnchorEl, setCompanyFolderAnchorEl] = useState<HTMLElement | null>(null);
  const [companyFolderValue, setCompanyFolderValue] = useState(companyFolder || '');
  const [isSavingCompanyFolder, setIsSavingCompanyFolder] = useState(false);
  const [selectedCompanyFolderPath, setSelectedCompanyFolderPath] = useState<string | null>(null);
  const isCompanyFolderPopoverOpen = Boolean(companyFolderAnchorEl);
  const [noticeAnchorEl, setNoticeAnchorEl] = useState<HTMLElement | null>(null);
  const noticeTextRef = useRef<HTMLSpanElement>(null);
  const [isNoticeTruncated, setIsNoticeTruncated] = useState(false);
  const [contactNoticeAnchorEl, setContactNoticeAnchorEl] = useState<HTMLElement | null>(null);
  const contactNoticeRef = useRef<HTMLSpanElement>(null);
  const [isContactNoticeTruncated, setIsContactNoticeTruncated] = useState(false);
  const [hasNotes, setHasNotes] = useState(false);
  const [hasContactNotes, setHasContactNotes] = useState(false);

  useEffect(() => {
    const el = noticeTextRef.current;
    if (el) {
      setIsNoticeTruncated(el.scrollHeight > el.clientHeight);
    }
  }, [valuedEntity?.valuedEntityNotice]);

  useEffect(() => {
    const el = contactNoticeRef.current;
    if (el) {
      setIsContactNoticeTruncated(el.scrollHeight > el.clientHeight);
    }
  }, [valuedEntity?.primaryContactNotice]);

  // Update local state when companyFolder prop changes
  const resolveCompanyFolderPath = useCallback(
    (folderRef?: string | null) => toWorkboardRelativePrefixFromStorageReference(folderRef, dropboxRoot),
    [dropboxRoot]
  );

  useEffect(() => {
    if (companyFolder !== undefined) {
      const relativePath = resolveCompanyFolderPath(companyFolder);
      setCompanyFolderValue(stripDropboxRootPrefix(relativePath, [dropboxRoot]));
      setSelectedCompanyFolderPath(relativePath || null);
    }
  }, [companyFolder, dropboxRoot, resolveCompanyFolderPath]);

  const resetCompanyFolderDraft = useCallback(() => {
    const relativePath = resolveCompanyFolderPath(companyFolder);
    setCompanyFolderValue(stripDropboxRootPrefix(relativePath, [dropboxRoot]));
    setSelectedCompanyFolderPath(relativePath || null);
  }, [companyFolder, dropboxRoot, resolveCompanyFolderPath]);

  const closeCompanyFolderPopover = useCallback(() => {
    setCompanyFolderAnchorEl(null);
    resetCompanyFolderDraft();
  }, [resetCompanyFolderDraft]);

  const openCompanyFolderPopover = (anchorEl: HTMLElement) => {
    if (!onUpdateCompanyFolder) {
      return;
    }
    const relativePath = resolveCompanyFolderPath(companyFolder);
    setCompanyFolderValue(stripDropboxRootPrefix(relativePath, [dropboxRoot]) || '');
    setSelectedCompanyFolderPath(relativePath || null);
    setCompanyFolderAnchorEl(anchorEl);
  };

  const handleCompanyFolderSync = async () => {
    if (!onUpdateCompanyFolder || isCompanyFolderSaveDisabled || !selectedCompanyFolderPath) {
      return;
    }

    const folderNameOnly = companyFolderValue.trim();
    const currentRelativeFolder = stripDropboxRootPrefix(resolveCompanyFolderPath(companyFolder), [dropboxRoot]);

    if (folderNameOnly === currentRelativeFolder) {
      closeCompanyFolderPopover();
      return;
    }

    setIsSavingCompanyFolder(true);
    try {
      await onUpdateCompanyFolder(folderNameOnly, selectedCompanyFolderPath);
      setCompanyFolderAnchorEl(null);
    } catch (error) {
      Logger.error('Error updating company folder', error);
    } finally {
      setIsSavingCompanyFolder(false);
    }
  };

  useEffect(() => {
    if (!valuedEntity?.valuedEntityId) {
      setHasNotes(false);
      setHasContactNotes(false);
      return;
    }

    const notesService = new AttioNotesService();

    notesService.getAllNotes(valuedEntity.valuedEntityId, 'companies').then(notes => {
      setHasNotes(notes.length > 0);
    });

    if (!valuedEntity.primaryContactId) {
      setHasContactNotes(false);
      return;
    }

    notesService.getAllNotes(valuedEntity.primaryContactId, 'people').then(notes => {
      setHasContactNotes(notes.length > 0);
    });
  }, [valuedEntity?.valuedEntityId, valuedEntity?.primaryContactId]);

  const handleCompanyFolderSelect = (directory: { name: string; path: string } | null) => {
    if (!directory) {
      setCompanyFolderValue('');
      setSelectedCompanyFolderPath(null);
      return;
    }
    const relativePath = directory.path.endsWith('/') ? directory.path : `${directory.path}/`;
    // Keep the full nested relative path for display/save, not only the leaf folder name.
    setCompanyFolderValue(relativePath.replace(/\/$/, '') || directory.name);
    setSelectedCompanyFolderPath(relativePath);
  };

  const statusConfig = getDropboxFolderStatusConfig(companyFolderStatus);
  const alternateNames = splitJoinedValues(valuedEntity?.companyAlternateName);
  const categories = splitJoinedValues(valuedEntity?.category);
  const websites = splitJoinedValues(valuedEntity?.companyWebsite);

  const displayCompanyFolderPath = companyFolder
    ? stripDropboxRootPrefix(resolveCompanyFolderPath(companyFolder), [dropboxRoot])
    : '';

  const companyFolderContent = (
    <Typography
      onClick={event => openCompanyFolderPopover(event.currentTarget)}
      sx={{
        mt: 0.5,
        width: 'fit-content',
        cursor: onUpdateCompanyFolder ? 'pointer' : 'default',
        color: onUpdateCompanyFolder ? 'primary.main' : 'text.primary',
        fontStyle: 'italic',
        '&:hover': onUpdateCompanyFolder ? { textDecoration: 'underline' } : {},
      }}
    >
      {displayCompanyFolderPath || 'Click here to set the folder path'}
    </Typography>
  );

  const companyFolderPopover = (
    <Popover
      open={isCompanyFolderPopoverOpen}
      anchorEl={companyFolderAnchorEl}
      onClose={closeCompanyFolderPopover}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{
        paper: {
          sx: FOLDER_PICKER_POPOVER_PAPER_SX,
        },
      }}
    >
      {/* Actions stay above the tree — Popover/Popper transform breaks position:sticky. */}
      <Box
        sx={{
          flexShrink: 0,
          px: 2,
          pt: 2,
          pb: 1.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <TextField
          sx={{ '& .MuiSelect-select:focus': { backgroundColor: 'transparent !important' } }}
          select
          label="Storage Root"
          value={dropboxRoot}
          onChange={e => onDropboxRootChange?.(e.target.value)}
          size="small"
          fullWidth
        >
          {dropboxRootOptions.map(root => (
            <MenuItem key={root} value={root}>
              {root}
            </MenuItem>
          ))}
        </TextField>
        <Typography
          variant="caption"
          color="text.secondary"
          title={`Path: ${dropboxRoot}/${companyFolderValue || '<folder>'}`}
          sx={FOLDER_PICKER_PATH_SX}
        >
          {`Path: ${dropboxRoot}/${companyFolderValue || '<folder>'}`}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button
            variant="contained"
            size="small"
            onClick={handleCompanyFolderSync}
            disabled={
              !selectedCompanyFolderPath ||
              !companyFolderValue.trim() ||
              isSavingCompanyFolder ||
              companyFolderStatus === 'syncing' ||
              isCompanyFolderSaveDisabled
            }
          >
            Save
          </Button>
          <Button variant="text" size="small" onClick={closeCompanyFolderPopover}>
            Cancel
          </Button>
        </Stack>
      </Box>

      <Box
        sx={{
          flex: '1 1 0%',
          minHeight: 0,
          overflow: 'auto',
          overscrollBehavior: 'contain',
          px: 2,
          py: 1.5,
        }}
      >
        <WorkboardFolderPicker
          key={isCompanyFolderPopoverOpen ? 'company-folder-open' : 'company-folder-closed'}
          dropboxRoot={dropboxRoot}
          companyName={valuedEntity?.companyName}
          savedFolderReference={companyFolderValue}
          selectedPath={selectedCompanyFolderPath}
          onSelect={handleCompanyFolderSelect}
        />
      </Box>
    </Popover>
  );

  return (
    <Section
      title="Valued Entity"
      headerAction={(
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
          <AttioNotesButton
            recordId={valuedEntity?.valuedEntityId}
            object="company"
            hasNotes={hasNotes} 
          />
        </Box>
      )}
    >
      <Container>
        <HeaderRow id="company-name">
          <BusinessIcon color="primary" fontSize="small" />
          <ConditionalLoader isLoading={isLoading && !valuedEntity?.companyName} width={150}>
            {valuedEntity?.companyUrl ? (
              <Link
                href={valuedEntity.companyUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <StyledLinkSpan>{valuedEntity.companyName || '-'}</StyledLinkSpan>
              </Link>
            ) : (
              <StyledLinkSpan>{valuedEntity?.companyName || '-'}</StyledLinkSpan>
            )}
          </ConditionalLoader>
        </HeaderRow>
        {valuedEntity?.valuedEntityNotice && (
          <Box>
            <Typography
              ref={noticeTextRef}
              variant="body2"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordBreak: 'break-word',
              }}
            >
              NOTICE: {valuedEntity.valuedEntityNotice}
            </Typography>
            {isNoticeTruncated && (
              <>
                <Typography
                  component="span"
                  variant="body2"
                  color="primary"
                  sx={{ cursor: 'pointer', fontSize: '0.75rem' }}
                  onClick={e => setNoticeAnchorEl(e.currentTarget)}
                >
                  show more
                </Typography>
                <Popover
                  open={Boolean(noticeAnchorEl)}
                  anchorEl={noticeAnchorEl}
                  onClose={() => setNoticeAnchorEl(null)}
                  anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                >
                  <Box sx={{ p: 2, maxWidth: 400 }}>
                    <Typography variant="body2">NOTICE: {valuedEntity.valuedEntityNotice}</Typography>
                  </Box>
                </Popover>
              </>
            )}
          </Box>
        )}
        <InfoRow>
          {alternateNames.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography id="alternate-names" variant="body2" color="text.secondary">
                {`Alternate Name${alternateNames.length > 1 ? 's:' : ':'}`}
              </Typography>
              <Typography variant="body2">
                {alternateNames.length === 1 ? alternateNames[0] : alternateNames.join(', ')}
              </Typography>
            </Box>
          )}
          <Box>
            <Typography id="category" variant="body2" color="text.secondary" mt={1}>
                Categories:
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <ConditionalLoader isLoading={isLoading && !valuedEntity?.category} width={150}>
              {categories.map(category => (
                <CategoryBadge key={category}>{category}</CategoryBadge>
              ))}
            </ConditionalLoader>
          </Box>
          {websites.length > 0 && (
            <Box>
              <Typography id="website" variant="body2" color="text.secondary" mt={1}>
                {`Website${websites.length > 1 ? 's:' : ':'}`}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {websites.map(domain => (
                  <Link
                    key={domain}
                    href={websiteHref(domain)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {domain}
                  </Link>
                ))}
              </Box>
            </Box>
          )}
          <ContactSection>
            <Box>
              <Typography id="dropbox-folder" variant="body2" color="text.secondary" mt={1}>
                Folder:
              </Typography>
            </Box>
            <ConditionalLoader isLoading={isLoading} width={200}>
              {companyFolderContent}
            </ConditionalLoader>
            {companyFolderPopover}
            {companyFolderStatus !== DROPBOX_FOLDER_STATUS.IDLE && (
              <Box sx={{ mt: 0.5 }}>
                <Chip size="small" color={statusConfig.color} label={statusConfig.label} />
              </Box>
            )}
            {companyFolderMatchPaths.length > 0 && (
              <Box sx={{ mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Found at:
                </Typography>
                {companyFolderMatchPaths.slice(0, 3).map(path => (
                  <Typography key={path} variant="caption" sx={{ display: 'block', wordBreak: 'break-all' }}>
                    {path}
                  </Typography>
                ))}
                {companyFolderMatchPaths.length > 3 && (
                  <Typography variant="caption" color="text.secondary">
                    +{companyFolderMatchPaths.length - 3} more
                  </Typography>
                )}
              </Box>
            )}
            {companyFolderSimilarMatchesCount > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {companyFolderSimilarMatchesCount} similar {companyFolderSimilarMatchesCount === 1 ? 'match' : 'matches'} found. Select the correct folder from the search results.
              </Typography>
            )}
          </ContactSection>
          <ContactCardsRow>
            <PersonnelRoleCard
              label="Primary Contact"
              name={valuedEntity?.primaryContact}
              isLoading={isLoading && !valuedEntity?.primaryContact}
              variant="emphasized"
              chipTone="accent"
              editPlacement="footer"
              headerContent={(
                <AttioNotesButton
                  recordId={valuedEntity?.primaryContactId}
                  object="person"
                  hasNotes={hasContactNotes}
                />
              )}
            />
            {(valuedEntity?.salesRep || isLoading) && (
              <PersonnelRoleCard
                label="Sales Rep"
                name={valuedEntity?.salesRep}
                imageSrc={valuedEntity?.salesRepImageSrc}
                isLoading={isLoading && !valuedEntity?.salesRep}
              />
            )}
          </ContactCardsRow>
          {valuedEntity?.primaryContactNotice && (
            <Box>
              <Typography
                ref={contactNoticeRef}
                variant="body2"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  wordBreak: 'break-word',
                }}
              >
                NOTICE: {valuedEntity.primaryContactNotice}
              </Typography>
              {isContactNoticeTruncated && (
                <>
                  <Typography
                    component="span"
                    variant="body2"
                    color="primary"
                    sx={{ cursor: 'pointer', fontSize: '0.75rem' }}
                    onClick={e => setContactNoticeAnchorEl(e.currentTarget)}
                  >
                    show more
                  </Typography>
                  <Popover
                    open={Boolean(contactNoticeAnchorEl)}
                    anchorEl={contactNoticeAnchorEl}
                    onClose={() => setContactNoticeAnchorEl(null)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  >
                    <Box sx={{ p: 2, maxWidth: 400 }}>
                      <Typography variant="body2">NOTICE: {valuedEntity.primaryContactNotice}</Typography>
                    </Box>
                  </Popover>
                </>
              )}
            </Box>
          )}
        </InfoRow>
      </Container>
    </Section>
  );
};

export default ValuedEntity;
